import { useEffect, useRef, useState } from "react";
import { decomposeInSiteTz, siteTz, todayInTz } from "@/lib/datetime";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import HelpTip from "@/components/HelpTip";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { blackoutDefaults } from "@/lib/constants";
import {
  createBlackout,
  listBaysLite,
  listSitesLite,
  updateBlackout,
} from "@/lib/api";
import { AuDateInput, AuTimeInput } from "@/components/AuDateTime";

export default function BlackoutForm({ open, onOpenChange, blackout, defaultSiteId, onSaved }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="blackout-form-dialog">
        <Inner
          key={blackout?.id || "new"}
          blackout={blackout}
          defaultSiteId={defaultSiteId}
          onSaved={(row) => {
            onSaved?.(row);
            onOpenChange(false);
          }}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function Inner({ blackout, defaultSiteId, onSaved, onCancel }) {
  const isEdit = !!blackout?.id;
  const [busy, setBusy] = useState(false);
  const [sites, setSites] = useState([]);
  const [bays, setBays] = useState([]);
  const autoDate = useRef(blackoutDefaults().start_date);

  const [v, setV] = useState(() => {
    if (!blackout) {
      const base = blackoutDefaults();
      return defaultSiteId ? { ...base, site_id: defaultSiteId } : base;
    }
    const tz = siteTz(blackout.site);
    const s = decomposeInSiteTz(blackout.start_time, tz);
    const e = decomposeInSiteTz(blackout.end_time, tz);
    return {
      site_id: blackout.site_id || "",
      bay_id: blackout.bay_id || "",
      title: blackout.title || "",
      reason: blackout.reason || "",
      start_date: s.dateStr,
      start_time: s.timeStr,
      end_date: e.dateStr,
      end_time: e.timeStr,
      is_full_day: !!blackout.is_full_day,
      client_id: blackout.client_id,
    };
  });

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listSitesLite();
      if (cancelled) return;
      setSites(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // New blackouts: keep the default dates on "today" at the chosen site.
  useEffect(() => {
    if (isEdit) return;
    const site = sites.find((s) => s.id === v.site_id);
    const t = todayInTz(siteTz(site || sites[0]));
    setV((s) => (s.start_date === autoDate.current && s.end_date === autoDate.current
      ? { ...s, start_date: t, end_date: t } : s));
    autoDate.current = t;
  }, [isEdit, sites, v.site_id]);

  useEffect(() => {
    let cancelled = false;
    if (!v.site_id) return;
    (async () => {
      const { data } = await listBaysLite(v.site_id, { includeInactive: true });
      if (cancelled) return;
      setBays(data || []);
    })();
    return () => { cancelled = true; };
  }, [v.site_id]);

  // When "full day" toggles on, force the times to whole-day.
  const setFullDay = (on) => {
    setV((s) => ({
      ...s,
      is_full_day: on,
      start_time: on ? "00:00" : s.start_time,
      end_time: on ? "23:59" : s.end_time,
    }));
  };

  const submit = async () => {
    if (!v.site_id) {
      toast.error("Site is required.");
      return;
    }
    if (!v.title) {
      toast.error("Title is required.");
      return;
    }
    if (`${v.end_date}T${v.end_time}` <= `${v.start_date}T${v.start_time}`) {
      toast.error("End must be after start.");
      return;
    }
    setBusy(true);
    const { data, error } = isEdit
      ? await updateBlackout(blackout.id, v)
      : await createBlackout(v);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(isEdit ? "Blackout updated." : "Blackout created.");
    onSaved?.(data);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle data-testid="blackout-form-title">
          {isEdit ? "Edit blackout" : "New blackout"}
        </DialogTitle>
        <DialogDescription>
          Blackouts close a bay (or the whole site) for a period. The Dock manager grid shades
          the time range — and the colour reflects who created the blackout.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 pt-2">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Site *">
            <Select value={v.site_id} onValueChange={(x) => { set("site_id", x); set("bay_id", ""); setBays([]); }}>
              <SelectTrigger data-testid="blackout-site"><SelectValue placeholder="Pick a site" /></SelectTrigger>
              <SelectContent>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} {s.code ? `· ${s.code}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Bay (leave blank for site-wide)" hint="Leave blank to close the entire site (public holidays, stock-take days). Pick a bay to close only one dock — useful for maintenance without blocking other bays.">
            <Select
              value={v.bay_id || "__all__"}
              onValueChange={(x) => set("bay_id", x === "__all__" ? "" : x)}
              disabled={!v.site_id}
            >
              <SelectTrigger data-testid="blackout-bay"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Whole site</SelectItem>
                {bays.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name} {b.code ? `· ${b.code}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Title *">
          <Input
            value={v.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="Public holiday, planned maintenance, …"
            data-testid="blackout-title"
          />
        </Field>

        <Field label="Reason / notes">
          <Textarea
            rows={2}
            value={v.reason}
            onChange={(e) => set("reason", e.target.value)}
            data-testid="blackout-reason"
          />
        </Field>

        <div className="flex items-center gap-2 pt-1">
          <Switch
            checked={v.is_full_day}
            onCheckedChange={setFullDay}
            data-testid="blackout-full-day"
          />
          <Label className="text-sm">Full day(s)</Label>
          <HelpTip id="blackout-full-day">
            When on, the start/end times are ignored and the bay or site is closed for every hour
            between the two dates. Turn off to close only a specific window (e.g. 12:00–13:30
            training).
          </HelpTip>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <Field label="Start date *">
            <AuDateInput
              value={v.start_date}
              onChange={(e) => set("start_date", e.target.value)}
              data-testid="blackout-start-date" required
            />
          </Field>
          <Field label="Start time">
            <AuTimeInput
              value={v.start_time}
              onChange={(e) => set("start_time", e.target.value)}
              disabled={v.is_full_day}
              data-testid="blackout-start-time"
            />
          </Field>
          <Field label="End date *">
            <AuDateInput
              value={v.end_date}
              onChange={(e) => set("end_date", e.target.value)}
              data-testid="blackout-end-date" required
            />
          </Field>
          <Field label="End time">
            <AuTimeInput
              value={v.end_time}
              onChange={(e) => set("end_time", e.target.value)}
              disabled={v.is_full_day}
              data-testid="blackout-end-time"
            />
          </Field>
        </div>
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onCancel} data-testid="blackout-cancel">
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy} data-testid="blackout-submit">
          {busy ? "Saving…" : isEdit ? "Save changes" : "Create blackout"}
        </Button>
      </DialogFooter>
    </>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
        {hint ? <HelpTip>{hint}</HelpTip> : null}
      </div>
      {children}
    </div>
  );
}
